import * as MsDyn365 from '@msdyn365-commerce/core';
import React from 'react';
import { IOrderHistoryTableData } from '../../actions/order-history/get-order-history.action';
import { NO_DATA_FALLBACK } from '../../configuration';
import { dateLocaleFormatter, toLocalDate } from '../../utilities/format-date';
import { getOrderDetailsPageUrl } from '../../utilities/get-order-details-page-url';
import { mapOverallDeliveryStatus } from '../../utilities/map-overall-status-code-to-text';
import {
    STATUS_CODE_CANCELED,
    STATUS_CODE_COMPLETED,
    STATUS_CODE_CONFIRMED,
    STATUS_CODE_GOODS_DISPATCHED,
    STATUS_CODE_ORDER,
    STATUS_CODE_PARTIALLY_DISPATCHED,
    STATUS_CODE_PARTIALLY_RECEIVED,
    STATUS_CODE_QUEUED,
    STATUS_CODE_REQUESTED,
    UNKNOWN_STATUS_CODE
} from '../../utilities/status-visual-group';
import { ILxsOrderOverviewViewProps, IOrderRowProps, IStatusBadgeProps } from './lxs-order-overview.data';

const CLASS_NAME = 'lxs-order-overview';

const STATUS_GROUPS = [
    { key: 'open', label: 'Open', codes: [STATUS_CODE_QUEUED, STATUS_CODE_REQUESTED, STATUS_CODE_CONFIRMED] },
    {
        key: 'in-progress',
        label: 'In Progress',
        codes: [STATUS_CODE_PARTIALLY_DISPATCHED, STATUS_CODE_GOODS_DISPATCHED, STATUS_CODE_PARTIALLY_RECEIVED]
    },
    { key: 'closed', label: 'Closed', codes: [STATUS_CODE_CANCELED, STATUS_CODE_COMPLETED] }
];

const StatusBadge: React.FC<IStatusBadgeProps> = ({ code, label }) => {
    const modifier = code || UNKNOWN_STATUS_CODE;
    return <span className={`${CLASS_NAME}__status-badge ${CLASS_NAME}__status-badge--${modifier}`}>{label}</span>;
};

const StatusSummaryCard: React.FC<{
    group: { key: string; label: string; codes: string[] };
    allOrders: IOrderHistoryTableData[];
    statusMap: ReturnType<typeof mapOverallDeliveryStatus>;
}> = ({ group, allOrders, statusMap }) => {
    const groupOrders = allOrders.filter(o => group.codes.includes(o.StateLogistics ?? ''));
    const total = groupOrders.length;
    const isEmpty = total === 0;

    const subItems = STATUS_CODE_ORDER.filter(code => group.codes.includes(code))
        .map(code => ({
            code,
            label: statusMap.getText(code),
            count: groupOrders.filter(o => o.StateLogistics === code).length
        }))
        .filter(item => item.count > 0);

    return (
        <div
            className={`${CLASS_NAME}__status-card ${CLASS_NAME}__status-card--${group.key} ${
                isEmpty ? `${CLASS_NAME}__status-card--empty` : ''
            }`}
        >
            <div className={`${CLASS_NAME}__status-card-header`}>
                <span className={`${CLASS_NAME}__status-card-label`}>{group.label}</span>
                <span className={`${CLASS_NAME}__status-card-total`}>{total}</span>
            </div>
            <ul className={`${CLASS_NAME}__status-card-items`} role='list'>
                {subItems.map(item => (
                    <li key={item.code} className={`${CLASS_NAME}__status-card-item`}>
                        <span className={`${CLASS_NAME}__order-row-dot ${CLASS_NAME}__order-row-dot--${item.code}`} />
                        <span className={`${CLASS_NAME}__status-card-item-label`}>{item.label}</span>
                        <span className={`${CLASS_NAME}__status-card-item-count`}>{item.count}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
};

const OrderRow: React.FC<IOrderRowProps> = ({ order, resources, context, statusLabel, orderDetailsUrl, localizationState }) => {
    const logisticsStatusCode = order.StateLogistics ?? '';
    const localDate = toLocalDate(order.ConfirmedDate);
    const formattedDate = dateLocaleFormatter(localizationState).formatDate(localDate);

    return (
        <a className={`${CLASS_NAME}__order-row`} href={orderDetailsUrl} role='listitem'>
            <div className={`${CLASS_NAME}__order-row-id`}>
                <span
                    className={`${CLASS_NAME}__order-row-dot ${CLASS_NAME}__order-row-dot--${logisticsStatusCode || UNKNOWN_STATUS_CODE}`}
                />
                <div className={`${CLASS_NAME}__order-row-id-content`}>
                    <div className={`${CLASS_NAME}__order-row-meta`}>
                        <span className={`${CLASS_NAME}__order-row-number`}>{order.OrderNumber}</span>
                        {formattedDate && <span className={`${CLASS_NAME}__order-row-date`}>{formattedDate}</span>}
                    </div>

                    <div className={`${CLASS_NAME}__order-row-po`}>
                        {`${resources.orderOverviewCustomerPoLabel} `}
                        <strong>{order.PoNumber ? order.PoNumber : NO_DATA_FALLBACK}</strong>
                    </div>
                </div>
            </div>

            <div className={`${CLASS_NAME}__order-row-addresses`}>
                {order.SoldToName && (
                    <span className={`${CLASS_NAME}__order-row-address`}>
                        <span className={`${CLASS_NAME}__order-row-address-label`}>{resources.orderOverviewSoldToLabel}</span>
                        <span className={`${CLASS_NAME}__order-row-address-value`}>{order.SoldToName}</span>
                    </span>
                )}
                {order.ShipToName && (
                    <span className={`${CLASS_NAME}__order-row-address`}>
                        <span className={`${CLASS_NAME}__order-row-address-label`}>{resources.orderOverviewShipToLabel}</span>
                        <span className={`${CLASS_NAME}__order-row-address-value`}>{order.ShipToName}</span>
                    </span>
                )}
            </div>

            <div className={`${CLASS_NAME}__order-row-right`}>
                <StatusBadge code={logisticsStatusCode} label={statusLabel} />
            </div>
        </a>
    );
};

const LxsOrderOverviewView: React.FC<ILxsOrderOverviewViewProps> = ({
    orders,
    isDataReady,
    hasError,
    config,
    resources,
    context,
    localizationState
}) => {
    const columns = Math.min(12, Math.max(6, config.columns ?? 12));
    const maxOrders = config.maxOrders ?? 10;
    const viewAllUrl = config.ordersPageLink?.linkUrl?.destinationUrl;
    const statusMap = mapOverallDeliveryStatus(resources);

    const allOrders = (orders ?? [])
        .sort((a, b) => {
            const aConfirmedDate = Date.parse(a.ConfirmedDate ?? '');
            const bConfirmedDate = Date.parse(b.ConfirmedDate ?? '');
            return (Number.isNaN(bConfirmedDate) ? 0 : bConfirmedDate) - (Number.isNaN(aConfirmedDate) ? 0 : aConfirmedDate);
        })
        .filter(order => order.StateLogistics !== UNKNOWN_STATUS_CODE)
        .slice(0, maxOrders);

    return (
        <section className={`${CLASS_NAME} col-md-${columns}${config.className ? ` ${config.className}` : ''}`}>
            <div className={`${CLASS_NAME}__header`}>
                <div className={`${CLASS_NAME}__header-text`}>
                    {config.preHeading && (
                        <MsDyn365.Text
                            className={`${CLASS_NAME}__preheading`}
                            text={config?.preHeading?.headingText}
                            tag={config?.preHeading?.tag || 'p'}
                        />
                    )}
                    {config.heading && (
                        <MsDyn365.Text
                            className={`${CLASS_NAME}__heading`}
                            text={config?.heading?.headingText}
                            tag={config?.heading?.tag || 'h2'}
                        />
                    )}
                </div>
                {viewAllUrl && (
                    <a
                        href={viewAllUrl}
                        className={`${CLASS_NAME}__view-all`}
                        aria-label={config.ordersPageLink?.ariaLabel ?? resources.orderOverviewViewAll}
                        target={config.ordersPageLink?.openInNewTab ? '_blank' : undefined}
                        rel={config.ordersPageLink?.openInNewTab ? 'noopener noreferrer' : undefined}
                    >
                        {resources.orderOverviewViewAll}
                    </a>
                )}
            </div>

            {!isDataReady && (
                <div className={`${CLASS_NAME}__loading`} aria-busy='true'>
                    {resources.orderOverviewLoadingLabel}
                </div>
            )}

            {isDataReady && hasError && (
                <div className={`${CLASS_NAME}__error`} role='alert'>
                    {resources.orderOverviewErrorMessage}
                </div>
            )}

            {isDataReady && !hasError && (
                <div className={`${CLASS_NAME}__body`}>
                    <div className={`${CLASS_NAME}__status-cards`}>
                        {STATUS_GROUPS.map(group => (
                            <StatusSummaryCard key={group.key} group={group} allOrders={allOrders} statusMap={statusMap} />
                        ))}
                    </div>

                    <div className={`${CLASS_NAME}__list`}>
                        <div className={`${CLASS_NAME}__list-header`}>
                            <span className={`${CLASS_NAME}__list-title`}>
                                {(resources.orderOverviewHeading ?? `Your last {count} orders`).replace(
                                    '{count}',
                                    String(allOrders.length)
                                )}
                            </span>
                        </div>

                        {allOrders.length === 0 ? (
                            <p className={`${CLASS_NAME}__empty`}>{resources.orderOverviewNoOrdersMessage}</p>
                        ) : (
                            <div className={`${CLASS_NAME}__order-list`} role='list'>
                                {allOrders.map(order => (
                                    <OrderRow
                                        key={order.OrderNumber}
                                        order={order}
                                        resources={resources}
                                        context={context}
                                        statusLabel={statusMap.getText(order.StateLogistics ?? '')}
                                        orderDetailsUrl={getOrderDetailsPageUrl(context.actionContext, Number(order.OrderNumber))}
                                        localizationState={localizationState}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
};

export default LxsOrderOverviewView;
